/**
 * El inicio del portal del colaborador (plan del portal, fase 3, D1).
 *
 * Lo recurrente a cero clics: la ficha resumida a la izquierda y, a la derecha,
 * lo pendiente primero —una firma se anuncia arriba con su acción— y después la
 * última liquidación, el saldo de vacaciones, las solicitudes y los documentos
 * recientes. Todo sale de **una sola llamada** (`home/`) más la ficha.
 *
 * Superficies: cada tarjeta es un `GlassPanel` de nivel 1 y los bloques de la
 * ficha van en nivel 2. El encabezado con la foto va sobre la tinta de la marca,
 * que es el único color sólido (plan, §5).
 *
 * ⚠️ Lo que no pide acción no se anuncia: sin firmas pendientes no hay aviso, y
 * las novedades aparecen recién cuando RRHH publique alguna (fase 4).
 */
import { Link } from 'react-router-dom'
import { CalendarDays, Clock, FileSignature, FileText, Inbox, Wallet } from 'lucide-react'
import type { ReactNode } from 'react'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { StatusBadge, LEAVE_REQUEST_STATUS, PAYSLIP_STATUS, VACATION_REQUEST_STATUS } from '@/components/ds/StatusBadge'
import { Monto } from '@/components/ds/Monto'
import { Skeleton } from '@/components/ds/Skeleton'
import { EmptyState } from '@/components/ds/EmptyState'
import { useMoney } from '@/lib/locale'
import { usePortalBase, usePortalFicha, usePortalInicio } from '../hooks/useEmployeePortal'
import { VerLiquidacion } from '../components/DescargarLiquidacion'
import { FichaResumida } from '../components/FichaResumida'
import { fechaCorta, mesYAnio, dias } from '../components/formatos'

function Titulo({ icono, children }: { icono: ReactNode; children: ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-content-muted">
      <span className="text-primary" aria-hidden="true">{icono}</span>
      {children}
    </h2>
  )
}

export default function HomePage() {
  const base = usePortalBase()
  const { data: ficha, isLoading: cargandoFicha } = usePortalFicha()
  const { data: inicio, isLoading, isError } = usePortalInicio()
  const money = useMoney()
  const nombrePila = ficha?.nombre.split(' ')[0]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)] items-start">
      <div className="lg:sticky lg:top-6">
        {cargandoFicha || !ficha
          ? <GlassPanel><Skeleton variant="block" /></GlassPanel>
          : <FichaResumida ficha={ficha} />}
      </div>

      <div className="flex flex-col gap-5 min-w-0">
        <div>
          <h1 className="text-2xl font-bold text-content">{nombrePila ? `Hola, ${nombrePila}` : 'Inicio'}</h1>
          <p className="text-sm text-content-muted mt-1">Lo recurrente de RRHH, a la vista.</p>
        </div>

        {isError && (
          <GlassPanel><EmptyState variant="error" title="No se pudo cargar el inicio" /></GlassPanel>
        )}

        {isLoading && <GlassPanel><Skeleton variant="block" /></GlassPanel>}

        {inicio && inicio.firmas_pendientes.cantidad > 0 && inicio.firmas_pendientes.primera && (
          <section
            aria-label="Pendientes"
            className="flex flex-wrap items-center gap-4 rounded-card border border-warning-border bg-warning-surface px-5 py-4"
          >
            <FileSignature size={22} className="text-warning-content shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-[12rem]">
              <p className="font-semibold text-warning-content-strong">
                {inicio.firmas_pendientes.cantidad === 1
                  ? '1 documento pendiente de firma'
                  : `${inicio.firmas_pendientes.cantidad} documentos pendientes de firma`}
              </p>
              <p className="text-sm text-warning-content">
                {inicio.firmas_pendientes.primera.documento} · enviado el {fechaCorta(inicio.firmas_pendientes.primera.pedida)}
              </p>
            </div>
            <Link
              to={`${base}/signatures?firmar=${inicio.firmas_pendientes.primera.id}`}
              className="inline-flex items-center rounded-lg bg-warning-fill hover:bg-warning-fill-hover px-4 py-2 text-sm font-semibold text-white"
            >
              Revisar y firmar
            </Link>
          </section>
        )}

        {inicio && inicio.aprobaciones_pendientes > 0 && (
          <section
            aria-label="Solicitudes del equipo por resolver"
            className="flex flex-wrap items-center gap-4 rounded-card border border-warning-border bg-warning-surface px-5 py-4"
          >
            <Inbox size={22} className="text-warning-content shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-[12rem]">
              <p className="font-semibold text-warning-content-strong">
                {inicio.aprobaciones_pendientes === 1
                  ? '1 solicitud del equipo por resolver'
                  : `${inicio.aprobaciones_pendientes} solicitudes del equipo por resolver`}
              </p>
              <p className="text-sm text-warning-content">Vacaciones o permisos que esperan la aprobación de la jefatura.</p>
            </div>
            <Link
              to={`${base}/my-team`}
              className="inline-flex items-center rounded-lg bg-warning-fill hover:bg-warning-fill-hover px-4 py-2 text-sm font-semibold text-white"
            >
              Revisar
            </Link>
          </section>
        )}

        {inicio && (
          <>
            <div className="grid gap-5 md:grid-cols-3">
              <GlassPanel as="section" aria-label="Última liquidación" className="flex flex-col gap-3">
                <Titulo icono={<Wallet size={16} />}>Última liquidación</Titulo>
                {inicio.ultima_liquidacion ? (
                  <>
                    <div>
                      <p className="text-lg font-bold text-content capitalize">
                        {mesYAnio(inicio.ultima_liquidacion.anio, inicio.ultima_liquidacion.mes)}
                      </p>
                      <StatusBadge status={inicio.ultima_liquidacion.estado} catalog={PAYSLIP_STATUS} />
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-content-muted">Líquido</span>
                      <Monto className="text-xl font-bold text-content">{money(inicio.ultima_liquidacion.liquido)}</Monto>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-auto pt-1">
                      <VerLiquidacion
                        payslipId={inicio.ultima_liquidacion.id}
                        periodLabel={mesYAnio(inicio.ultima_liquidacion.anio, inicio.ultima_liquidacion.mes)}
                      />
                      <Link to={`${base}/payslips`} className="text-xs font-semibold text-primary hover:underline">Ver todas</Link>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-content-muted">Todavía no hay liquidaciones publicadas.</p>
                )}
              </GlassPanel>

              <GlassPanel as="section" aria-label="Vacaciones" className="flex flex-col gap-3">
                <Titulo icono={<CalendarDays size={16} />}>Vacaciones disponibles</Titulo>
                <p className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-content tabular-nums">{dias(inicio.vacaciones.disponibles)}</span>
                  <span className="text-sm text-content-muted">días hábiles</span>
                </p>
                <p className="text-sm text-content-muted">
                  {inicio.vacaciones.proxima
                    ? `Próximas: ${fechaCorta(inicio.vacaciones.proxima.desde)} al ${fechaCorta(inicio.vacaciones.proxima.hasta)} (aprobadas)`
                    : 'Sin vacaciones aprobadas por delante.'}
                </p>
                <Link
                  to={`${base}/time-off`}
                  className="mt-auto inline-flex justify-center rounded-lg bg-primary hover:bg-primary-hover px-4 py-2 text-sm font-semibold text-on-primary"
                >
                  Solicitar vacaciones
                </Link>
              </GlassPanel>

              <GlassPanel as="section" aria-label="Mis solicitudes" className="flex flex-col gap-3">
                <Titulo icono={<Clock size={16} />}>Mis solicitudes</Titulo>
                {inicio.solicitudes.length ? (
                  <ul className="flex flex-col gap-2">
                    {inicio.solicitudes.map((s, i) => (
                      <li key={`${s.tipo}-${s.desde}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-content truncate">
                          {s.tipo === 'vacaciones' ? 'Vacaciones' : (s.detalle || 'Permiso')} · {fechaCorta(s.desde)}
                          {s.hasta !== s.desde ? `–${fechaCorta(s.hasta)}` : ''}
                        </span>
                        <StatusBadge
                          status={s.estado}
                          catalog={s.tipo === 'vacaciones' ? VACATION_REQUEST_STATUS : LEAVE_REQUEST_STATUS}
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-content-muted">Sin solicitudes registradas.</p>
                )}
                <Link to={`${base}/time-off`} className="mt-auto text-xs font-semibold text-primary hover:underline">
                  Pedir un permiso
                </Link>
              </GlassPanel>
            </div>

            <GlassPanel
              as="section"
              aria-label="Documentos recientes"
              title="Documentos recientes"
              actions={<Link to={`${base}/documents`} className="text-xs font-semibold text-primary hover:underline">Ver todos</Link>}
            >
              {inicio.documentos_recientes.length ? (
                <ul className="divide-y divide-divider">
                  {inicio.documentos_recientes.map(d => (
                    <li key={d.id}>
                      <Link
                        to={`${base}/documents`}
                        className="flex items-center gap-3 py-2.5 text-sm hover:text-primary"
                      >
                        <FileText size={18} className="text-content-muted shrink-0" aria-hidden="true" />
                        <span className="flex-1 truncate text-content">{d.nombre}</span>
                        <span className="text-content-muted tabular-nums">{fechaCorta(d.fecha)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-content-muted">Todavía no hay documentos en la carpeta.</p>
              )}
            </GlassPanel>
          </>
        )}
      </div>
    </div>
  )
}
